import axios from 'axios';
import { IAuthUserInfo } from '../types/userInfo';
const apiUrl = process.env.PORTAL_URL;
const agentId = process.env.AGENT_AI_ID;
const proxyPath = '/api/middleware-lia';

async function getMainUserInfo(
  userInfo?: IAuthUserInfo
): Promise<Partial<IAuthUserInfo & { cpf?: string }>> {
  if (!userInfo) {
    return {};
  }

  const {
    birthdate,
    gender: genderType,
    sexuality,
    preferred_username,
    given_name,
    upn,
    nomeCompleto,
  } = userInfo || {};

  return {
    cpf: preferred_username ?? upn,
    name: nomeCompleto ?? given_name,
    birthdate: birthdate,
    gender: sexuality ?? genderType,
  };
}

export async function updateMetadata(
  token: string | undefined,
  userInfo?: IAuthUserInfo,
  conversationId?: string | null | undefined
) {
  if (!conversationId) return;
  const mainUserInfo = await getMainUserInfo(userInfo);
  if (userInfo) {
    try {
      await axios.put(
        `${apiUrl}${proxyPath}/v1/lia/conversation/update/${conversationId}`,
        { ...mainUserInfo, ...(token && { token }), jwe: true },
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token && {
              Authorization: `Bearer ${token}`,
            }),
          },
        }
      );
    } catch (err) {
      console.log('err ====> ', err);
    }
  }

  return;
}

export async function liaResponseByAudio(
  audioBlob: Blob,
  conversationId?: string | null | undefined
): Promise<{
  lia_response: string;
  user_transcriptionID: string;
  transcription: string;
}> {
  try {
    const formData = new FormData();
    formData.append('file', audioBlob, 'audio.webm');

    const response = await axios.post(
      `${apiUrl}${proxyPath}/v1/lia/response-by-audio/${conversationId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    if (response?.data) {
      return response.data;
    }

    throw new Error('Resposta inválida da API de transcrição.');
  } catch (error: any) {
    console.error('Erro na transcrição de áudio:', error);
    throw new Error(
      error?.response?.data?.message || 'Falha ao transcrever áudio'
    );
  }
}

export async function getAIAudioReponse(
  iaText: string,
  userInfo?: IAuthUserInfo,
  token?: string | undefined,
  conversationId?: string | null | undefined
): Promise<{ text: string; audioUrl: string | null }> {
  updateMetadata(token, userInfo, conversationId);

  try {
    const ttsResponse = await axios.post(
      `${apiUrl}${proxyPath}/v1/lia/voice/tts/${conversationId}`,
      {},
      {
        headers: {
          'Content-Type': 'application/json',
        },
        responseType: 'blob',
      }
    );

    const audioBlob: any = ttsResponse.data;
    const audioUrl = URL.createObjectURL(audioBlob);

    return {
      text: iaText,
      audioUrl,
    };
  } catch (error: any) {
    error.message = `Error to get audio response: ${error}`;
    throw error.message;
  }
}

export async function getIAResposta(
  text: string,
  userInfo?: IAuthUserInfo,
  token?: string | undefined,
  conversationId?: string | null | undefined
): Promise<any> {
  if (!conversationId) {
    conversationId = await getConversationId(token, userInfo);

    const newConversationRequisition = await createIAConversation(
      token,
      userInfo,
      conversationId
    );

    if (newConversationRequisition?.status === 201) {
      conversationId = newConversationRequisition?.data?.id;
    }
  }

  updateMetadata(token, userInfo, conversationId);

  const response = await axios.post(
    `${apiUrl}${proxyPath}/v1/lia/conversation/stream/${conversationId}`,
    { text: text },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  return response?.data;
}

export async function createIAConversation(
  token?: string | undefined,
  userInfo?: IAuthUserInfo,
  conversationId?: string | null | undefined
): Promise<any> {
  const mainUserInfo = await getMainUserInfo(userInfo);
  updateMetadata(token, userInfo, conversationId);
  const response = await axios.post(
    `${apiUrl}${proxyPath}/v1/lia/conversation/create/${agentId}`,
    {
      ...mainUserInfo,
      token,
      jwe: true,
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  return response;
}

export async function getConversationId(
  token?: string | undefined,
  userInfo?: any
): Promise<string | undefined> {
  const isCurrentSessionValid = checkConversationSessionLifetime();

  if (isCurrentSessionValid) {
    const conversationIdSession = localStorage.getItem('conversationId');
    if (conversationIdSession) {
      const conversationData = JSON.parse(conversationIdSession);

      return conversationData.conversationId;
    }
  }
  const newConversationRequisition = await createIAConversation(
    token,
    userInfo
  );
  if (newConversationRequisition?.status === 201) {
    const conversationId = newConversationRequisition?.data?.id;

    if (conversationId) renewConversationSession(conversationId);

    return conversationId || agentId;
  }
}

function checkConversationSessionLifetime(): boolean {
  const conversationIdSession = localStorage.getItem('conversationId');

  if (conversationIdSession) {
    const conversationData = JSON.parse(conversationIdSession || '{}');

    if (conversationData.timeStamp) {
      const currentTime = Date.now();
      const timeDifference = currentTime - conversationData.timeStamp;

      const sessionLifetime = 24 * 60 * 60 * 1000; // 24h in milliseconds

      return timeDifference < sessionLifetime;
    }
    return false;
  }

  return false;
}
function renewConversationSession(conversationId: string): void {
  const conversationData = {
    conversationId: conversationId,
    timeStamp: Date.now(),
  };

  localStorage.setItem('conversationId', JSON.stringify(conversationData));
}

export async function sendFeedback(
  conversationId: string,
  feedback: number,
  token?: string,
  userInfo?: any
): Promise<any> {
  updateMetadata(token, userInfo, conversationId);
  try {
    const response = await axios.post(
      `${apiUrl}${proxyPath}/v1/lia/conversation/survey/${conversationId}`,
      { nota: feedback },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response?.data;
  } catch (error) {
    console.error('Erro ao enviar feedback:', error);
    throw error;
  }
}
